import { Link, useLocation, useParams } from 'react-router-dom';

import { useAuthStore } from '@/stores/auth-store';

interface ProjectNavItem {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
}

function getProjectNavItems(projectKey: string, isAdmin: boolean): ProjectNavItem[] {
  const base = '/projects/' + encodeURIComponent(projectKey);
  const items: ProjectNavItem[] = [
    {
      key: 'activity',
      label: 'PRs',
      path: base,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'review',
      label: 'Review',
      path: base + '/review',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
    },
    {
      key: 'artifacts',
      label: 'Artifacts',
      path: base + '/artifacts',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
        </svg>
      ),
    },
    {
      key: 'download',
      label: 'Download',
      path: base + '/download',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
    },
    {
      key: 'state',
      label: 'State',
      path: base + '/state',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'servers',
      label: 'Servers',
      path: base + '/servers',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
        </svg>
      ),
    },
    {
      key: 'deploy',
      label: 'Deploy',
      path: base + '/deploy',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11.25l-3-3m0 0l-3 3m3-3v7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: 'configure',
      label: 'Configure',
      path: base + '/configure',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      key: 'databases',
      label: 'Databases',
      path: base + '/databases',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
    },
    {
      key: 'history',
      label: 'History',
      path: base + '/history',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m-9-3a9 9 0 119 9m-9-9H3m3-3l-3 3 3 3" />
        </svg>
      ),
    },
  ];

  if (isAdmin) {
    items.push({
      key: 'settings',
      label: 'Settings',
      path: base + '/settings',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    });
  }

  return items;
}

/**
 * Project sub-navigation sidebar.
 * Compact icon-focused layout matching the main sidebar style,
 * using the brand dark colour scheme to visually distinguish it.
 * Full height, sticky positioning alongside the main sidebar.
 */
export function ProjectSidebar() {
  const { projectKey } = useParams<{ projectKey: string }>();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const key = projectKey ?? '';

  // Check admin access: global Admin OR project-level Admin
  const isAdmin =
    user?.role === 'Admin' ||
    (user?.projectRoles ?? []).some(
      (entry) => entry.project === key && entry.role === 'Admin'
    );

  const navItems = getProjectNavItems(key, isAdmin);

  const basePath = '/projects/' + encodeURIComponent(key);

  // Determine active item: exact match for the base path (activity), startsWith for others
  const activeItem = navItems.reduce<ProjectNavItem | null>((best, item) => {
    if (item.path === basePath) {
      if (location.pathname === basePath || location.pathname === basePath + '/') {
        return item;
      }
      return best;
    }
    if (location.pathname.startsWith(item.path)) {
      if (!best || item.path.length > best.path.length) {
        return item;
      }
    }
    return best;
  }, null);

  return (
    <nav
      aria-label="Project navigation"
      className="sticky top-0 flex-shrink-0 w-16 h-full bg-accent-50 dark:bg-accent-950 border-r border-accent-200 dark:border-accent-900"
    >
      <div className="flex h-full flex-col items-center">
        {/* Project key badge at the top */}
        <div className="flex items-center justify-center w-full py-3 border-b border-accent-200 dark:border-accent-800">
          <span
            className="text-[9px] font-bold text-accent-700 dark:text-accent-300 uppercase tracking-wider truncate max-w-[56px] text-center"
            title={key}
          >
            {key.length > 6 ? key.slice(0, 6) : key}
          </span>
        </div>

        {/* Nav items */}
        <ul className="flex-1 w-full py-3 space-y-1" role="list">
          {navItems.map((item) => {
            const isActive = activeItem?.key === item.key;
            return (
              <li key={item.key}>
                <Link
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={item.label}
                  className={[
                    'flex flex-col items-center justify-center gap-0.5 rounded-md mx-1 px-1 py-2',
                    'min-h-[52px]',
                    'transition-colors duration-150',
                    'focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-1 focus:ring-offset-accent-50 dark:focus:ring-offset-accent-950',
                    isActive
                      ? 'bg-accent-200 text-accent-900 dark:bg-accent-800 dark:text-white'
                      : 'text-accent-700 hover:bg-accent-100 hover:text-accent-900 dark:text-accent-300 dark:hover:bg-accent-900 dark:hover:text-white',
                  ].join(' ')}
                >
                  {item.icon}
                  <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}